import { Env } from "@/env";
import { useQuery } from "react-query";
import { useParams } from "react-router-dom";
import { toast } from "sonner";
import { useUser } from "../user/use-user";
import { fetchServer } from "../utils";
import { LorasImagesType, LorasType } from "./type";

let autoRefresh: any = 0;

export const useLora = (id: string) => {
  const lora = useQuery(
    ["loras", id],
    async () => {
      if (!id) return null;
      return await fetchServer<LorasType>("/api/loras/" + id);
    },
    { enabled: !!id }
  );
  return lora;
};

export const useLoraImages = (id: string) => {
  const images = useQuery(
    ["loras_images", id],
    async () => {
      if (!id) return null;
      return await fetchServer<LorasImagesType[]>(`/api/loras/${id}/images`);
    },
    { enabled: !!id }
  );
  return images;
};

export const useLoras = () => {
  const { refresh } = useUser();
  const { site } = useParams<{ site: string }>();

  const loras = useQuery("loras", async () => {
    return await fetchServer<LorasType[]>("/api/loras");
  });

  const create = async (
    main: string,
    photos: string[],
    lora: Pick<LorasType, "name" | "type" | "subtype" | "pro" | "technology">
  ) => {
    try {
      const res = await fetchServer<LorasType>("/api/loras", {
        main,
        photos,
        lora,
      });
      // Refresh loras
      loras.refetch();
      // Refresh user data (credits)
      refresh();
      return res;
    } catch (e) {
      console.error(e);
      toast.error("Failed to create lora");
      throw e;
    }
  };

  const update = async (
    id: string,
    change: {
      details: Partial<LorasType["details"]>;
      friends?: boolean;
      allow_nsfw_from_other?: boolean;
      public?: boolean;
    }
  ) => {
    try {
      const res = await fetchServer<LorasType>("/api/loras/" + id, change);
      // Refresh loras
      loras.refetch();
      return res;
    } catch (e) {
      console.error(e);
      toast.error("Failed to update lora");
      throw e;
    }
  };

  const remove = async (id: string) => {
    try {
      await fetchServer("/api/loras/" + id + "/delete", {});
      document.location.href = `/${site}/`;
      // Refresh loras
      loras.refetch();
    } catch (e) {
      console.error(e);
      toast.error("Failed to remove lora");
      throw e;
    }
  };

  if (loras.data?.some((a) => a.preview_status !== "completed")) {
    clearTimeout(autoRefresh);
    autoRefresh = setInterval(() => loras.refetch(), 5000);
  }

  return {
    loras,
    remove,
    create,
    update,
    refresh: () => loras.refetch(),
  };
};

export const getLoraPreview = (id: string, status?: "completed" | string) => {
  if (status && status !== "completed") {
    return "data:image/gif;base64,R0lGODlhAQABAIAAAAAAAAAAACH5BAAAAAAALAAAAAABAAEAAAICTAEAOw==";
  }
  return Env.server + "/api/loras/" + id + "/preview?t=" + status;
};
